import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

import { LanguageProvider } from "@/contexts/LanguageContext";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";

import RentalFlow from "@/pages/RentalFlow";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AdminDashboard from "@/pages/AdminDashboard";
import StationDashboard from "@/pages/StationDashboard";
import ContractDetail from "@/pages/ContractDetail";
import Confirmation from "@/pages/Confirmation";
import MyContracts from "@/pages/MyContracts";
import CalendarPage from "@/pages/CalendarPage";
import Profile from "@/pages/Profile";
import LocationPage from "@/pages/LocationPage";
import { AGBPage, PrivacyPage, ImprintPage } from "@/pages/LegalPages";
import QuotePage from "@/pages/QuotePage";
import RequestsAdminPage from "@/pages/RequestsAdminPage";
import ContactWidget from "@/components/ContactWidget";
import Footer from "@/components/Footer";
import PromoBanner from "@/components/PromoBanner";
import GoogleAnalytics from "@/components/GoogleAnalytics";

const RoleRoute = ({ roles, children }) => {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="rs-app min-h-screen flex items-center justify-center bg-white">
        <div className="rs-overline">…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!roles.includes(user.role)) {
    if (user.role === "station_manager") return <Navigate to="/station" replace />;
    if (user.role === "admin") return <Navigate to="/admin" replace />;
    if (user.role === "customer") return <Navigate to="/my" replace />;
    return <Navigate to="/login" replace />;
  }
  return children;
};

// PromoBanner is only visible on public marketing pages — not in admin/station/customer dashboards.
const PromoBannerGate = () => {
  const path = typeof window !== "undefined" ? window.location.pathname : "/";
  const blockedPrefixes = ["/admin", "/station", "/my", "/login", "/register", "/confirmation"];
  if (blockedPrefixes.some((p) => path.startsWith(p))) return null;
  return <PromoBanner />;
};

function App() {
  return (
    <div className="App rs-app">
      <LanguageProvider>
        <AuthProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<RentalFlow />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/calendar" element={<CalendarPage />} />
              <Route path="/confirmation/:id" element={<Confirmation />} />
              <Route path="/standort/:slug" element={<LocationPage />} />
              <Route path="/agb" element={<AGBPage />} />
              <Route path="/datenschutz" element={<PrivacyPage />} />
              <Route path="/impressum" element={<ImprintPage />} />
              <Route path="/offerte/:id/:token" element={<QuotePage />} />
              <Route path="/admin/anfragen" element={<RequestsAdminPage />} />

              <Route path="/admin" element={<RoleRoute roles={["admin"]}><AdminDashboard /></RoleRoute>} />
              <Route path="/admin/contracts/:id" element={<RoleRoute roles={["admin"]}><ContractDetail /></RoleRoute>} />

              <Route path="/station" element={<RoleRoute roles={["station_manager", "admin"]}><StationDashboard /></RoleRoute>} />
              <Route path="/station/contracts/:id" element={<RoleRoute roles={["station_manager", "admin"]}><ContractDetail /></RoleRoute>} />

              <Route path="/my" element={<RoleRoute roles={["customer"]}><MyContracts /></RoleRoute>} />
              <Route path="/my/profile" element={<RoleRoute roles={["customer"]}><Profile /></RoleRoute>} />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <ContactWidget />
            <PromoBannerGate />
            <GoogleAnalytics />
          </BrowserRouter>
        </AuthProvider>
      </LanguageProvider>
    </div>
  );
}

export default App;
